<template>
    <v-dialog v-model="visible" fullscreen scrollable transition="dialog-bottom-transition">
        <v-card class="finance-card">
            <!-- Header -->
            <div class="card-header">
                <div class="left-section">
                    <div class="header-badge">{{ $t('finance.header.badge') }}</div>
                    <h2>{{ $t('finance.header.title') }}</h2>
                    <p class="header-description">
                        {{ $t('finance.header.description') }}
                    </p>
                </div>
                <div class="right-section">
                    <button @click="closeDialog" class="dialog-close-btn">
                        <v-icon>mdi-close</v-icon>
                    </button>
                </div>
            </div>

            <!-- Main Content -->
            <div class="finance-container">
                <!-- Value Propositions -->
                <div class="value-section">
                    <div class="section-header">
                        <h3>{{ $t('finance.valuePropositions.title') }}</h3>
                        <p>{{ $t('finance.valuePropositions.subtitle') }}</p>
                    </div>

                    <v-row class="value-grid">
                        <!-- Flexible Pricing -->
                        <v-col cols="12" md="6" lg="4" class="pa-3">
                            <div class="value-card pricing">
                                <div class="value-icon">
                                    <v-icon size="40" color="white">mdi-currency-eur</v-icon>
                                </div>
                                <h4>{{ $t('finance.valuePropositions.flexiblePricing.title') }}</h4>
                                <p>{{ $t('finance.valuePropositions.flexiblePricing.description') }}</p>
                                <div class="value-list">
                                    <div class="value-item">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('finance.valuePropositions.flexiblePricing.feature1') }}</span>
                                    </div>
                                    <div class="value-item">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('finance.valuePropositions.flexiblePricing.feature2') }}</span>
                                    </div>
                                    <div class="value-item">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('finance.valuePropositions.flexiblePricing.feature3') }}</span>
                                    </div>
                                    <div class="value-item">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('finance.valuePropositions.flexiblePricing.feature4') }}</span>
                                    </div>
                                </div>
                            </div>
                        </v-col>

                        <!-- Fast Onboarding -->
                        <v-col cols="12" md="6" lg="4" class="pa-3">
                            <div class="value-card onboarding">
                                <div class="value-icon">
                                    <v-icon size="40" color="white">mdi-rocket-launch-outline</v-icon>
                                </div>
                                <h4>{{ $t('finance.valuePropositions.rapidImplementation.title') }}</h4>
                                <p>{{ $t('finance.valuePropositions.rapidImplementation.description') }}</p>
                                <div class="value-list">
                                    <div class="value-item">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('finance.valuePropositions.rapidImplementation.feature1') }}</span>
                                    </div>
                                    <div class="value-item">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('finance.valuePropositions.rapidImplementation.feature2') }}</span>
                                    </div>
                                    <div class="value-item">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('finance.valuePropositions.rapidImplementation.feature3') }}</span>
                                    </div>
                                    <div class="value-item">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('finance.valuePropositions.rapidImplementation.feature4') }}</span>
                                    </div>
                                </div>
                            </div>
                        </v-col>

                        <!-- Unlock Value -->
                        <v-col cols="12" md="6" lg="4" class="pa-3">
                            <div class="value-card roi">
                                <div class="value-icon">
                                    <v-icon size="40" color="white">mdi-treasure-chest</v-icon>
                                </div>
                                <h4>{{ $t('finance.valuePropositions.unlockValue.title') }}</h4>
                                <p>{{ $t('finance.valuePropositions.unlockValue.description') }}</p>
                                <div class="value-list">
                                    <div class="value-item">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('finance.valuePropositions.unlockValue.feature1') }}</span>
                                    </div>
                                    <div class="value-item">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('finance.valuePropositions.unlockValue.feature2') }}</span>
                                    </div>
                                    <div class="value-item">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('finance.valuePropositions.unlockValue.feature3') }}</span>
                                    </div>
                                    <div class="value-item">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('finance.valuePropositions.unlockValue.feature4') }}</span>
                                    </div>
                                </div>
                            </div>
                        </v-col>
                    </v-row>
                </div>

                <!-- Pricing Models -->
                <div class="pricing-section">
                    <div class="section-header">
                        <h3>{{ $t('finance.licensingModels.title') }}</h3>
                        <p>{{ $t('finance.licensingModels.subtitle') }}</p>
                    </div>

                    <v-row class="pricing-grid">
                        <!-- CAPEX Model -->
                        <v-col cols="12" lg="4" class="pa-3">
                            <div class="pricing-card capex">
                                <div class="pricing-badge">
                                    <span>{{ $t('finance.licensingModels.capex.badge') }}</span>
                                </div>
                                <div class="pricing-icon">
                                    <v-icon size="48" color="white">mdi-bank</v-icon>
                                </div>
                                <h4>{{ $t('finance.licensingModels.capex.title') }}</h4>
                                <p class="pricing-description">{{ $t('finance.licensingModels.capex.description') }}</p>
                                <div class="pricing-features">
                                    <div class="feature">
                                        <v-icon size="18">mdi-check-bold</v-icon>
                                        <span>{{ $t('finance.licensingModels.capex.feature1') }}</span>
                                    </div>
                                    <div class="feature">
                                        <v-icon size="18">mdi-check-bold</v-icon>
                                        <span>{{ $t('finance.licensingModels.capex.feature2') }}</span>
                                    </div>
                                    <div class="feature">
                                        <v-icon size="18">mdi-check-bold</v-icon>
                                        <span>{{ $t('finance.licensingModels.capex.feature3') }}</span>
                                    </div>
                                    <div class="feature">
                                        <v-icon size="18">mdi-check-bold</v-icon>
                                        <span>{{ $t('finance.licensingModels.capex.feature4') }}</span>
                                    </div>
                                </div>
                                <div class="pricing-best-for">
                                    <span class="label">{{ $t('finance.licensingModels.bestForLabel') }}</span>
                                    <span class="value">{{ $t('finance.licensingModels.capex.bestFor') }}</span>
                                </div>
                            </div>
                        </v-col>

                        <!-- OPEX Model -->
                        <v-col cols="12" lg="4" class="pa-3">
                            <div class="pricing-card opex">
                                <div class="pricing-badge">
                                    <span>{{ $t('finance.licensingModels.opex.badge') }}</span>
                                </div>
                                <div class="pricing-icon">
                                    <v-icon size="48" color="white">mdi-calendar-sync</v-icon>
                                </div>
                                <h4>{{ $t('finance.licensingModels.opex.title') }}</h4>
                                <p class="pricing-description">{{ $t('finance.licensingModels.opex.description') }}</p>
                                <div class="pricing-features">
                                    <div class="feature">
                                        <v-icon size="18">mdi-check-bold</v-icon>
                                        <span>{{ $t('finance.licensingModels.opex.feature1') }}</span>
                                    </div>
                                    <div class="feature">
                                        <v-icon size="18">mdi-check-bold</v-icon>
                                        <span>{{ $t('finance.licensingModels.opex.feature2') }}</span>
                                    </div>
                                    <div class="feature">
                                        <v-icon size="18">mdi-check-bold</v-icon>
                                        <span>{{ $t('finance.licensingModels.opex.feature3') }}</span>
                                    </div>
                                    <div class="feature">
                                        <v-icon size="18">mdi-check-bold</v-icon>
                                        <span>{{ $t('finance.licensingModels.opex.feature4') }}</span>
                                    </div>
                                </div>
                                <div class="pricing-best-for">
                                    <span class="label">{{ $t('finance.licensingModels.bestForLabel') }}</span>
                                    <span class="value">{{ $t('finance.licensingModels.opex.bestFor') }}</span>
                                </div>
                            </div>
                        </v-col>

                        <!-- Hybrid Model -->
                        <v-col cols="12" lg="4" class="pa-3">
                            <div class="pricing-card hybrid">
                                <div class="pricing-badge">
                                    <span>{{ $t('finance.licensingModels.hybrid.badge') }}</span>
                                </div>
                                <div class="pricing-icon">
                                    <v-icon size="48" color="white">mdi-swap-horizontal</v-icon>
                                </div>
                                <h4>{{ $t('finance.licensingModels.hybrid.title') }}</h4>
                                <p class="pricing-description">{{ $t('finance.licensingModels.hybrid.description') }}</p>
                                <div class="pricing-features">
                                    <div class="feature">
                                        <v-icon size="18">mdi-check-bold</v-icon>
                                        <span>{{ $t('finance.licensingModels.hybrid.feature1') }}</span>
                                    </div>
                                    <div class="feature">
                                        <v-icon size="18">mdi-check-bold</v-icon>
                                        <span>{{ $t('finance.licensingModels.hybrid.feature2') }}</span>
                                    </div>
                                    <div class="feature">
                                        <v-icon size="18">mdi-check-bold</v-icon>
                                        <span>{{ $t('finance.licensingModels.hybrid.feature3') }}</span>
                                    </div>
                                    <div class="feature">
                                        <v-icon size="18">mdi-check-bold</v-icon>
                                        <span>{{ $t('finance.licensingModels.hybrid.feature4') }}</span>
                                    </div>
                                </div>
                                <div class="pricing-best-for">
                                    <span class="label">{{ $t('finance.licensingModels.bestForLabel') }}</span>
                                    <span class="value">{{ $t('finance.licensingModels.hybrid.bestFor') }}</span>
                                </div>
                            </div>
                        </v-col>
                    </v-row>
                </div>

                <!-- Billing Options -->
                <div class="billing-section">
                    <div class="section-header">
                        <h3>{{ $t('finance.billingOptions.title') }}</h3>
                        <p>{{ $t('finance.billingOptions.subtitle') }}</p>
                    </div>

                    <div class="billing-grid">
                        <div class="billing-card">
                            <div class="billing-icon">
                                <v-icon size="32" color="white">mdi-desk</v-icon>
                            </div>
                            <h4>{{ $t('finance.billingOptions.perDesk.title') }}</h4>
                            <p>{{ $t('finance.billingOptions.perDesk.description') }}</p>
                            <div class="billing-example">
                                <span class="example-label">{{ $t('finance.billingOptions.exampleLabel') }}</span>
                                <span class="example-value">{{ $t('finance.billingOptions.perDesk.example') }}</span>
                            </div>
                        </div>

                        <div class="billing-card">
                            <div class="billing-icon">
                                <v-icon size="32" color="white">mdi-floor-plan</v-icon>
                            </div>
                            <h4>{{ $t('finance.billingOptions.perSquareMeter.title') }}</h4>
                            <p>{{ $t('finance.billingOptions.perSquareMeter.description') }}</p>
                            <div class="billing-example">
                                <span class="example-label">{{ $t('finance.billingOptions.exampleLabel') }}</span>
                                <span class="example-value">{{ $t('finance.billingOptions.perSquareMeter.example') }}</span>
                            </div>
                        </div>

                        <div class="billing-card">
                            <div class="billing-icon">
                                <v-icon size="32" color="white">mdi-account-group</v-icon>
                            </div>
                            <h4>{{ $t('finance.billingOptions.perEmployee.title') }}</h4>
                            <p>{{ $t('finance.billingOptions.perEmployee.description') }}</p>
                            <div class="billing-example">
                                <span class="example-label">{{ $t('finance.billingOptions.exampleLabel') }}</span>
                                <span class="example-value">{{ $t('finance.billingOptions.perEmployee.example') }}</span>
                            </div>
                        </div>

                        <div class="billing-card">
                            <div class="billing-icon">
                                <v-icon size="32" color="white">mdi-cog-outline</v-icon>
                            </div>
                            <h4>{{ $t('finance.billingOptions.custom.title') }}</h4>
                            <p>{{ $t('finance.billingOptions.custom.description') }}</p>
                            <div class="billing-example">
                                <span class="example-label">{{ $t('finance.billingOptions.contactLabel') }}</span>
                                <span class="example-value">{{ "sales@pinestack.com" }}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Financial Flow -->
                <div class="flow-section">
                    <div class="section-header">
                        <h3>{{ $t('finance.valueJourney.title') }}</h3>
                        <p>{{ $t('finance.valueJourney.subtitle') }}</p>
                    </div>

                    <div class="financial-flow">
                        <div class="flow-step">
                            <div class="step-number">1</div>
                            <div class="step-icon">
                                <v-icon size="36" color="white">mdi-account-cash</v-icon>
                            </div>
                            <h4>{{ $t('finance.valueJourney.step1.title') }}</h4>
                            <p>{{ $t('finance.valueJourney.step1.description') }}</p>
                        </div>

                        <div class="flow-arrow">
                            <v-icon size="32">mdi-arrow-right-thick</v-icon>
                        </div>

                        <div class="flow-step">
                            <div class="step-number">2</div>
                            <div class="step-icon">
                                <v-icon size="36" color="white">mdi-cogs</v-icon>
                            </div>
                            <h4>{{ $t('finance.valueJourney.step2.title') }}</h4>
                            <p>{{ $t('finance.valueJourney.step2.description') }}</p>
                        </div>

                        <div class="flow-arrow">
                            <v-icon size="32">mdi-arrow-right-thick</v-icon>
                        </div>

                        <div class="flow-step">
                            <div class="step-number">3</div>
                            <div class="step-icon">
                                <v-icon size="36" color="white">mdi-chart-line</v-icon>
                            </div>
                            <h4>{{ $t('finance.valueJourney.step3.title') }}</h4>
                            <p>{{ $t('finance.valueJourney.step3.description') }}</p>
                        </div>

                        <div class="flow-arrow">
                            <v-icon size="32">mdi-arrow-right-thick</v-icon>
                        </div>

                        <div class="flow-step">
                            <div class="step-number">4</div>
                            <div class="step-icon">
                                <v-icon size="36" color="white">mdi-check-decagram</v-icon>
                            </div>
                            <h4>{{ $t('finance.valueJourney.step4.title') }}</h4>
                            <p>{{ $t('finance.valueJourney.step4.description') }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </v-card>
    </v-dialog>
</template>

<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const visible = ref(false);

function open() { 
    visible.value = true; 
}

function closeDialog() { 
    visible.value = false; 
}

defineExpose({ open, close: closeDialog });
</script>

<style scoped>
/* Card Base */
.finance-card {
    background: linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%);
    min-height: 100vh;
    position: relative;
}

/* Header */
.card-header {
    background: linear-gradient(135deg, #0A1929 0%, #1A2F42 100%);
    padding: 32px 40px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    position: sticky;
    top: 0;
    z-index: 100;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.left-section {
    flex: 1;
}

.header-badge {
    display: inline-block;
    padding: 6px 16px;
    background: rgba(30, 90, 142, 0.2);
    border: 1px solid rgba(30, 90, 142, 0.3);
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1px;
    color: #60a5fa;
    margin-bottom: 16px;
}

.card-header h2 {
    font-size: 32px;
    font-weight: 800;
    color: #ffffff;
    margin: 0 0 8px 0;
    letter-spacing: -0.5px;
}

.header-description {
    font-size: 15px;
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.5;
    margin: 0;
}

.right-section {
    margin-left: 24px;
}

.dialog-close-btn {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);
}

.dialog-close-btn:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.3);
    transform: scale(1.05);
}

.dialog-close-btn .v-icon {
    color: white;
}

/* Main Container */
.finance-container {
    padding: 40px;
    max-width: 1600px;
    margin: 0 auto;
}

/* Section Styling */
.value-section,
.pricing-section,
.billing-section,
.flow-section {
    margin-bottom: 56px;
}

.section-header {
    text-align: center;
    margin-bottom: 40px;
}

.section-header h3 {
    font-size: 32px;
    font-weight: 800;
    color: #1f2937;
    margin: 0 0 12px 0;
    letter-spacing: -0.5px;
}

.section-header p {
    font-size: 16px;
    color: #6b7280;
    margin: 0;
}

/* Value Cards */
.value-card {
    background: white;
    border-radius: 20px;
    padding: 28px;
    height: 100%;
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    border: 2px solid transparent;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
    text-align: center;
}

.value-card:hover {
    transform: translateY(-8px);
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.12);
}

.value-card.pricing:hover { border-color: #06b6d4; }
.value-card.onboarding:hover { border-color: #1E5A8E; }
.value-card.roi:hover { border-color: #fbbf24; }

.value-icon {
    width: 80px;
    height: 80px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
    transition: transform 0.4s ease;
}

.value-card:hover .value-icon {
    transform: scale(1.1) rotate(-5deg);
}

.value-card.pricing .value-icon {
    background: linear-gradient(135deg, #06b6d4 0%, #22d3ee 100%);
}

.value-card.onboarding .value-icon {
    background: linear-gradient(135deg, #1E5A8E 0%, #2B7BC4 100%);
}

.value-card.roi .value-icon {
    background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
}

.value-card h4 {
    font-size: 20px;
    font-weight: 700;
    color: #1f2937;
    margin: 0 0 12px 0;
}

.value-card > p {
    font-size: 14px;
    color: #6b7280;
    line-height: 1.7;
    margin: 0 0 20px 0;
}

.value-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
    text-align: left;
}

.value-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #4b5563;
    font-weight: 500;
}

.value-item .v-icon {
    color: #10b981;
}

/* Pricing Cards */
.pricing-card {
    background: white;
    border-radius: 24px;
    padding: 32px;
    height: 100%;
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    border: 2px solid #e5e7eb;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
    position: relative;
    text-align: center;
}

.pricing-card:hover {
    transform: translateY(-8px);
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.12);
    border-color: #1E5A8E;
}

.pricing-card.featured {
    border-color: #1E5A8E;
    box-shadow: 0 8px 32px rgba(30, 90, 142, 0.15);
}

.featured-badge {
    position: absolute;
    top: -12px;
    left: 50%;
    transform: translateX(-50%);
    padding: 6px 16px;
    background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
    color: white;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.5px;
    border-radius: 20px;
}

.pricing-badge {
    display: inline-block;
    padding: 8px 20px;
    background: #f3f4f6;
    border-radius: 12px;
    margin-bottom: 20px;
}

.pricing-badge span {
    font-size: 14px;
    font-weight: 700;
    color: #1E5A8E;
    letter-spacing: 1px;
}

.pricing-icon {
    width: 96px;
    height: 96px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 24px;
    transition: transform 0.4s ease;
}

.pricing-card:hover .pricing-icon {
    transform: scale(1.1) rotate(-10deg);
}

.pricing-card.capex .pricing-icon {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.pricing-card.opex .pricing-icon {
    background: linear-gradient(135deg, #1E5A8E 0%, #2B7BC4 100%);
}

.pricing-card.hybrid .pricing-icon {
    background: linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%);
}

.pricing-card h4 {
    font-size: 24px;
    font-weight: 700;
    color: #1f2937;
    margin: 0 0 12px 0;
}

.pricing-description {
    font-size: 14px;
    color: #6b7280;
    line-height: 1.7;
    margin: 0 0 24px 0;
}

.pricing-features {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 24px;
    text-align: left;
}

.feature {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 14px;
    color: #4b5563;
    font-weight: 500;
}

.feature .v-icon {
    color: #10b981;
}

.pricing-best-for {
    padding-top: 24px;
    border-top: 2px solid #f3f4f6;
    text-align: left;
}

.pricing-best-for .label {
    display: block;
    font-size: 12px;
    font-weight: 700;
    color: #1E5A8E;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
}

.pricing-best-for .value {
    font-size: 13px;
    color: #4b5563;
    font-weight: 500;
}

/* Billing Cards */
.billing-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 24px;
}

.billing-card {
    background: white;
    border-radius: 20px;
    padding: 28px;
    text-align: center;
    transition: all 0.3s ease;
    border: 2px solid #f3f4f6;
}

.billing-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
    border-color: #1E5A8E;
}

.billing-icon {
    width: 64px;
    height: 64px;
    border-radius: 12px;
    background: linear-gradient(135deg, #1E5A8E 0%, #2B7BC4 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 16px;
}

.billing-card h4 {
    font-size: 18px;
    font-weight: 700;
    color: #1f2937;
    margin: 0 0 8px 0;
}

.billing-card > p {
    font-size: 14px;
    color: #6b7280;
    margin: 0 0 16px 0;
}

.billing-example {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding-top: 16px;
    border-top: 2px solid #f3f4f6;
}

.example-label {
    font-size: 11px;
    font-weight: 700;
    color: #9ca3af;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.example-value {
    font-size: 14px;
    font-weight: 600;
    color: #1E5A8E;
}

/* Financial Flow */
.financial-flow {
    background: white;
    border-radius: 24px;
    padding: 48px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
    border: 1px solid rgba(0, 0, 0, 0.04);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 24px;
    overflow-x: auto;
}

.flow-step {
    position: relative;
    flex-shrink: 0;
    text-align: center;
    max-width: 200px;
}

.step-number {
    position: absolute;
    top: -12px;
    left: 50%;
    transform: translateX(-50%);
    width: 32px;
    height: 32px;
    background: linear-gradient(135deg, #1E5A8E 0%, #2B7BC4 100%);
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 700;
    box-shadow: 0 4px 12px rgba(30, 90, 142, 0.3);
}

.step-icon {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: linear-gradient(135deg, #1E5A8E 0%, #2B7BC4 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 16px;
    transition: transform 0.3s ease;
}

.flow-step:hover .step-icon {
    transform: scale(1.1);
}

.flow-step h4 {
    font-size: 16px;
    font-weight: 700;
    color: #1f2937;
    margin: 0 0 8px 0;
}

.flow-step p {
    font-size: 13px;
    color: #6b7280;
    margin: 0;
    line-height: 1.5;
}

.flow-arrow {
    color: #1E5A8E;
    flex-shrink: 0;
}

/* Responsive */
@media (max-width: 1024px) {
    .finance-container {
        padding: 32px 24px;
    }

    .financial-flow {
        flex-direction: column;
        padding: 32px 24px;
    }

    .flow-arrow {
        transform: rotate(90deg);
    }
}

@media (max-width: 768px) {
    .card-header {
        padding: 24px;
        flex-direction: column;
        gap: 20px;
    }

    .card-header h2 {
        font-size: 24px;
    }

    .header-description {
        font-size: 14px;
    }

    .right-section {
        margin-left: 0;
        align-self: flex-end;
    }

    .finance-container {
        padding: 24px 16px;
    }

    .section-header h3 {
        font-size: 24px;
    }

    .financial-flow {
        padding: 24px 16px;
    }

    .billing-grid {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 480px) {
    .card-header h2 {
        font-size: 20px;
    }

    .finance-container {
        padding: 20px 12px;
    }
}

/* Scrollbar */
.finance-container::-webkit-scrollbar {
    width: 8px;
}

.finance-container::-webkit-scrollbar-track {
    background: #f3f4f6;
}

.finance-container::-webkit-scrollbar-thumb {
    background: #9ca3af;
    border-radius: 4px;
}

.finance-container::-webkit-scrollbar-thumb:hover {
    background: #6b7280;
}

.financial-flow::-webkit-scrollbar {
    height: 6px;
}

.financial-flow::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 3px;
}
</style>